// Types and helpers for emailing a property's listing agent (Final Decision > Actions > Email Listing Agent).

export interface EmailVariable {
  key: string;
  label: string;
}

export interface EmailClientOption {
  key: string;
  label: string;
  hint: string;
  // 'file' = a draft file for a desktop app; 'compose' = a browser compose window plus a zip of documents
  mode: 'file' | 'compose';
}

export interface EmailTemplateInfo {
  body: string;
  default_body: string;
  subject_pattern: string;
  variables: EmailVariable[];
  email_client: string;
  email_clients: EmailClientOption[];
}

// Web mail cannot open a draft file or be handed attachments, so it gets a compose window and a zip.
export const WEB_MAIL_CLIENTS = [ 'gmail', 'outlook_web' ];
export const isWebMail = (client: string | undefined): boolean => !!client && WEB_MAIL_CLIENTS.includes(client);

export interface ComposeLink {
  compose_url: string;
  to: string | null;
  subject: string;
  attachments: string[];
  attachments_url: string;
}

// Whether one property can be emailed, and what is missing when it cannot.
export interface ListingEmailStatus {
  property_id: string;
  ready: boolean;
  missing: string[];
  // Things worth knowing that do not block the draft (not listed, no agent email...)
  notes?: string[];
  agent_email: string | null;
}

export const statusByProperty = (results: ListingEmailStatus[]): Record<string, ListingEmailStatus> =>
  Object.fromEntries(results.map((row) => [ String(row.property_id), row ]));

// The browser downloads this as an .eml draft (To, subject, letter and the contract attached).
export const listingEmailDraftUrl = (listSortId: string, propertyId: string): string =>
  `/listing_emails/draft?list_sort_id=${encodeURIComponent(listSortId)}&property_id=${encodeURIComponent(propertyId)}`;

// One zip with a draft email per selected property (up to LISTING_EMAIL_BULK_MAX).
export const LISTING_EMAIL_BULK_MAX = 25;
// plain = a general Draft Email: no contract, no listing agent (To line left empty).
export const listingEmailBulkUrl = (listSortId: string, propertyIds: string[], plain = false): string =>
  `/listing_emails/bulk?list_sort_id=${encodeURIComponent(listSortId)}&${propertyIds
    .map((id) => `property_ids[]=${encodeURIComponent(id)}`)
    .join('&')}${plain ? '&plain=1' : ''}`;

// Make Wholesale Offer: a draft email to the wholesaler of each selected wholesale deal, offering the Max Bid.
export const wholesaleOfferDraftsUrl = (listSortId: string, propertyIds: string[]): string =>
  `/wholesale_offers/drafts?list_sort_id=${encodeURIComponent(listSortId)}&${propertyIds
    .map((id) => `property_ids[]=${encodeURIComponent(id)}`)
    .join('&')}`;

// Fetches the offer drafts and saves the file. When the server says why it cannot (no Max Bid, not a
// wholesale deal...) that message is thrown so it can be shown, instead of a broken download page.
export const downloadWholesaleOffers = async (url: string): Promise<void> => {
  const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json, */*' } });
  if (!response.ok) {
    let message = 'We could not make the offer email. Please try again.';
    try {
      const data = await response.json();
      if (Array.isArray(data?.errors) && data.errors.length > 0) message = data.errors.join(' ');
    } catch {
      // keep the general message
    }
    throw new Error(message);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : 'wholesale-offer.eml';
  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

// "{{agent_name}}" etc. as shown to the user.
export const variableTag = (key: string): string => `{{${key}}}`;

// Inserts text at the cursor (or replaces the selection) and says where the cursor should go next.
export const insertAtCursor = (text: string, insert: string, start: number, end: number) => ({
  text: text.slice(0, start) + insert + text.slice(end),
  cursor: start + insert.length,
});

// ---- Documents sent with every listing-agent email (proof of funds etc.) ----

export type EmailDocKind = 'proof_of_funds' | 'additional';

export interface EmailDocument {
  id: string;
  kind: EmailDocKind;
  name: string;
  filename: string;
  content_type: string;
  byte_size: number;
  attach_by_default: boolean;
}

export interface EmailDocumentList {
  results: EmailDocument[];
  max_additional: number;
  max_mb: number;
}

export const ALLOWED_DOC_TYPES = '.pdf,.png,.jpg,.jpeg,.docx';

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Total size of the documents that will be attached to every email (the contract adds to this).
export const attachedBytes = (docs: EmailDocument[]): number =>
  docs.filter((d) => d.attach_by_default).reduce((sum, d) => sum + d.byte_size, 0);

export const emailDocumentUrl = (id: string): string => `/email_attachments/${id}/file`;
